package com.milta.feature.admin.data.remote.dto

import com.milta.feature.admin.domain.entity.AdminClaudeUsage
import com.milta.feature.admin.domain.entity.AdminClaudeUsageAccount
import com.milta.feature.admin.domain.entity.AdminClaudeUsageEvent
import com.milta.feature.admin.domain.entity.AdminClaudeUsageEvents
import com.milta.feature.admin.domain.entity.AdminClaudeUsageOrganisation
import com.milta.feature.admin.domain.entity.AdminClaudeUsageSummary
import kotlinx.serialization.Serializable

// Field names match the backend's camelCase JSON exactly (System.Text.Json default) — see
// Milta.Application.Admin.Dtos.AdminClaudeUsageResponseDto in the milta (backend) repo. Backend
// `decimal` cost fields deserialize fine as plain Kotlin Double here (System.Text.Json emits a
// plain JSON number, no special decimal encoding).

@Serializable
data class AdminClaudeUsageResponseDto(
    val summary: AdminClaudeUsageSummaryDto,
    val accounts: List<AdminClaudeUsageAccountDto>,
    val organisations: List<AdminClaudeUsageOrganisationDto>,
)

@Serializable
data class AdminClaudeUsageSummaryDto(
    val totalInputTokens: Long,
    val totalOutputTokens: Long,
    val totalCalls: Int,
    val failureRatePercent: Double,
    val activeAccountCount: Int,
    val activeOrganisationCount: Int,
    val totalEstimatedCostEur: Double,
)

@Serializable
data class AdminClaudeUsageAccountDto(
    val accountId: String,
    val firstName: String,
    val lastName: String,
    val enterpriseId: String?,
    val enterpriseName: String?,
    val inputTokens: Long,
    val outputTokens: Long,
    val percentOfTotal: Double,
    val failureRatePercent: Double,
    val tokensBySourceEndpoint: Map<String, Long>,
    val estimatedCostEur: Double,
    val averageEstimatedCostPerCallEur: Double,
)

@Serializable
data class AdminClaudeUsageOrganisationDto(
    val enterpriseId: String,
    val enterpriseName: String,
    val inputTokens: Long,
    val outputTokens: Long,
    val percentOfTotal: Double,
    val failureRatePercent: Double,
    val tokensBySourceEndpoint: Map<String, Long>,
    val estimatedCostEur: Double,
    val averageEstimatedCostPerCallEur: Double,
    val members: List<AdminClaudeUsageAccountDto>,
)

@Serializable
data class AdminClaudeUsageEventsResponseDto(
    val totalCount: Int,
    val page: Int,
    val pageSize: Int,
    val events: List<AdminClaudeUsageEventDto>,
)

@Serializable
data class AdminClaudeUsageEventDto(
    val id: String,
    val createdAt: String,
    val sourceEndpoint: String,
    val inputTokens: Int,
    val outputTokens: Int,
    val outcome: String,
    val responseText: String?,
    val hasSourceFile: Boolean,
    val sourceFileExpired: Boolean,
    val sourceFilePresignedUrl: String?,
    val estimatedCostEur: Double,
)

fun AdminClaudeUsageResponseDto.toDomain() = AdminClaudeUsage(
    summary = summary.toDomain(),
    accounts = accounts.map { it.toDomain() },
    organisations = organisations.map { it.toDomain() },
)

fun AdminClaudeUsageSummaryDto.toDomain() = AdminClaudeUsageSummary(
    totalInputTokens = totalInputTokens,
    totalOutputTokens = totalOutputTokens,
    totalCalls = totalCalls,
    failureRatePercent = failureRatePercent,
    activeAccountCount = activeAccountCount,
    activeOrganisationCount = activeOrganisationCount,
    totalEstimatedCostEur = totalEstimatedCostEur,
)

fun AdminClaudeUsageAccountDto.toDomain() = AdminClaudeUsageAccount(
    accountId = accountId,
    firstName = firstName,
    lastName = lastName,
    enterpriseId = enterpriseId,
    enterpriseName = enterpriseName,
    inputTokens = inputTokens,
    outputTokens = outputTokens,
    percentOfTotal = percentOfTotal,
    failureRatePercent = failureRatePercent,
    tokensBySourceEndpoint = tokensBySourceEndpoint,
    estimatedCostEur = estimatedCostEur,
    averageEstimatedCostPerCallEur = averageEstimatedCostPerCallEur,
)

fun AdminClaudeUsageOrganisationDto.toDomain() = AdminClaudeUsageOrganisation(
    enterpriseId = enterpriseId,
    enterpriseName = enterpriseName,
    inputTokens = inputTokens,
    outputTokens = outputTokens,
    percentOfTotal = percentOfTotal,
    failureRatePercent = failureRatePercent,
    tokensBySourceEndpoint = tokensBySourceEndpoint,
    estimatedCostEur = estimatedCostEur,
    averageEstimatedCostPerCallEur = averageEstimatedCostPerCallEur,
    members = members.map { it.toDomain() },
)

fun AdminClaudeUsageEventsResponseDto.toDomain() = AdminClaudeUsageEvents(
    totalCount = totalCount,
    page = page,
    pageSize = pageSize,
    events = events.map { it.toDomain() },
)

fun AdminClaudeUsageEventDto.toDomain() = AdminClaudeUsageEvent(
    id = id,
    createdAt = createdAt,
    sourceEndpoint = sourceEndpoint,
    inputTokens = inputTokens,
    outputTokens = outputTokens,
    outcome = outcome,
    responseText = responseText,
    hasSourceFile = hasSourceFile,
    sourceFileExpired = sourceFileExpired,
    sourceFilePresignedUrl = sourceFilePresignedUrl,
    estimatedCostEur = estimatedCostEur,
)
